import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { getSupportState } from 'Douleutaras/redux/data/settings/supportSlice';

export interface ExitIntent {
  isOpen: boolean;
  open(): void;
  close(): void;
}

export const useExitIntent = (enabled: boolean): ExitIntent => {
  const dispatch = useAppDispatch();
  const supportLoaded = useAppSelector(state => state.support.loaded);
  const [ isOpen, setIsOpen ] = useState<boolean>(false);
  // Where focus was when the panel took over, so «Continue» can hand it back.
  const focusBeforeOpen = useRef<HTMLElement>(undefined);

  // The panel focuses itself; returning to the form has to put focus back, or the
  // customer resumes at the top of the document with the step they were on unannounced.
  useEffect(() => {
    if (isOpen) {
      focusBeforeOpen.current = document.activeElement as HTMLElement;

      return;
    }

    focusBeforeOpen.current?.focus?.();
    focusBeforeOpen.current = undefined;
  }, [isOpen]);

  // Only the surfaces with a retention panel read support availability, and they need it
  // by the time the × is pressed — fetching it at app boot made every unrelated route
  // pay for an answer it never looked at.
  useEffect(() => {
    if (enabled && !supportLoaded) {
      void dispatch(getSupportState());
    }
  }, [enabled, supportLoaded]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, open, close };
};
